import { ReactNode, Suspense, useState } from "react";
import { Fieldset, Legend } from "@/components/catalyst/fieldset";
import { useFormikContext } from "formik";
import { useTranslations } from "use-intl";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import { clsx } from "clsx";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import {
  AdditionalSettingsSections,
  InsuranceField,
  RouteAndVehicleFields,
  SupplierFieldsSkeleton,
  AvailabilityDateFields,
  HireUnitFields,
  TripDefaultsSync,
} from "@/app/Relocations/Forms/RelocationForm";
import {
  useHireUnitLabels,
  useSupplierFormDefaults,
} from "@/app/Relocations/Forms/relocationFormHooks";
import { RelocationTripCard } from "@/app/Relocations/Forms/RelocationTripCard";
import { RelocationDistanceFields } from "@/app/Relocations/Forms/RelocationDistanceFields";
import { RelocationInclusionForm } from "@/app/Relocations/Forms/RelocationInclusionForm";
import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { Steps } from "@/lib/Components/Steps/Steps";
import { useFormSteps } from "@/lib/Components/Steps/useFormSteps";
import { Button } from "@/components/catalyst/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeRecordQuery } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { vehicleRecordQuery } from "@/app/Vehicles/GraphQL/vehicleRecordQuery";
import { supplierInsuranceOptionRecord } from "@/app/Suppliers/GraphQL/supplierInsuranceOptionRecord";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "@/components/catalyst/description-list";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Measurement, RelocationInclusionType } from "@/gql/graphql";
import { FormErrorSummary } from "@/app/Relocations/Components/FormErrorSummary";

const stepFields = {
  route: [
    "supplier.connect",
    "departureOffice.connect",
    "deliveryOfficeIds",
    "vehicle.connect",
    "quantity",
    "lines",
  ],
  hire: [
    "available_from_date",
    "available_to_date",
    "hire_units_allowed",
    "extra_hire_units_allowed",
    "minimum_hire_units",
    "distance_allowed",
    "extra_distance_allowed_per_extra_hire_unit",
    "excess_distance_rate",
  ],
  inclusions: ["supplierInsuranceOption.connect", "inclusions"],
  review: [],
} as const;

type StepId = keyof typeof stepFields;

export function RelocationStepsForm({
  onSwitchDialog,
}: {
  onSwitchDialog?: () => void;
}) {
  const t = useTranslations("relocation.form");
  const tDialog = useTranslations("common.dialog");
  const { values, initialValues, isSubmitting } =
    useFormikContext<RelocationFormValues>();
  const { close, reject } = useDialogState();
  const {
    stepOrder,
    step,
    stepIndex,
    setStepIndex,
    isLast,
    submitStep,
    goBack,
  } = useFormSteps(stepFields);

  const hasSupplier = !!values.supplier.connect;
  const showSupplierInput =
    step === "route" && !initialValues.supplier?.connect;

  const steps = stepOrder.map((id) => ({
    id,
    name: t(`steps.${id}`),
  }));

  return (
    <div className="col-span-full space-y-8">
      <Steps steps={steps} currentIndex={stepIndex} onSelect={setStepIndex} />

      {showSupplierInput ? (
        <div className="border-l-4 border-blue-500 pl-4">
          <SupplierInput name="supplier.connect" label={t("supplier")} />
        </div>
      ) : null}

      {hasSupplier ? (
        <Suspense fallback={<SupplierFieldsSkeleton />}>
          <StepContent step={step} onEditStep={setStepIndex} />
        </Suspense>
      ) : (
        <p className="text-sm text-gray-500">
          {t("steps.chooseSupplierFirst")}
        </p>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 pt-6">
        <div className="flex items-center gap-3">
          <Button
            plain
            type="button"
            onClick={() => {
              reject?.(false);
              close();
            }}
          >
            {tDialog("cancel")}
          </Button>
          {onSwitchDialog ? (
            <button
              type="button"
              onClick={onSwitchDialog}
              className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
            >
              {t("steps.useClassicForm")}
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {stepIndex > 0 ? (
            <Button outline type="button" onClick={goBack}>
              <ArrowLeftIcon data-slot="icon" />
              {t("steps.back")}
            </Button>
          ) : null}
          {isLast ? (
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              onClick={submitStep}
            >
              <PlusIcon data-slot="icon" />
              {t("steps.submit")}
            </Button>
          ) : (
            <Button type="submit" disabled={!hasSupplier} onClick={submitStep}>
              {t("steps.next")}
              <ArrowRightIcon data-slot="icon" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepContent({
  step,
  onEditStep,
}: {
  step: StepId;
  onEditStep: (index: number) => void;
}) {
  const t = useTranslations("relocation.form");
  useSupplierFormDefaults();

  return (
    <>
      <Suspense fallback={null}>
        <TripDefaultsSync />
      </Suspense>

      {step === "route" ? <RouteAndVehicleFields /> : null}

      {step === "hire" ? (
        <div className="space-y-6">
          <Suspense fallback={<Skeleton className="h-[290px]" />}>
            <RelocationTripCard />
          </Suspense>
          <StepSection title={t("steps.sectionDates")}>
            <AvailabilityDateFields />
          </StepSection>
          <StepSection title={t("steps.sectionHire")}>
            <HireUnitFields fieldClassName="col-span-3" />
          </StepSection>
          <StepSection title={t("steps.sectionDistance")}>
            <RelocationDistanceFields />
          </StepSection>
        </div>
      ) : null}

      {step === "inclusions" ? (
        <div className="space-y-8">
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            <InsuranceField />
          </div>
          <RelocationInclusionForm />
        </div>
      ) : null}

      {step === "review" ? <ReviewStep onEditStep={onEditStep} /> : null}
    </>
  );
}

function StepSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Fieldset>
      <Legend>{title}</Legend>
      <div className="mt-3 grid grid-cols-3 gap-4 sm:grid-cols-12">
        {children}
      </div>
    </Fieldset>
  );
}

function ReviewStep({ onEditStep }: { onEditStep: (index: number) => void }) {
  const t = useTranslations("relocation.form.steps");
  const tRelocation = useTranslations("relocation");
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<RelocationFormValues>();
  const { unitLabel } = useHireUnitLabels();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const departureOfficeId = values.departureOffice?.connect ?? "";
  const deliveryOfficeId = values.deliveryOfficeIds?.[0] ?? "";
  const vehicleId = values.vehicle?.connect ?? "";
  const insuranceId = values.supplierInsuranceOption?.connect ?? "";

  const { data: departure } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: departureOfficeId },
    { enabled: !!departureOfficeId },
  );
  const { data: delivery } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: deliveryOfficeId },
    { enabled: !!deliveryOfficeId },
  );
  const { data: vehicle } = useGqlQuery(
    vehicleRecordQuery,
    { id: vehicleId },
    { enabled: !!vehicleId },
  );
  const { data: insurance } = useGqlQuery(
    supplierInsuranceOptionRecord,
    { id: insuranceId },
    { enabled: !!insuranceId },
  );

  const additionalDestinations = (values.deliveryOfficeIds?.length ?? 0) - 1;
  const inclusionLabels = (values.inclusions?.upsert ?? []).map((inclusion) =>
    inclusion.type === RelocationInclusionType.Fuel && inclusion.has_free_tank
      ? tRelocation("inclusions.oneFreeTank")
      : tRelocation(`inclusions.type.${inclusion.type}`),
  );
  const distanceUnit =
    values.measurement === Measurement.Imperial ? "mi" : "km";

  function editButton(index: number) {
    return (
      <button
        type="button"
        className="ml-2 text-xs font-medium text-blue-600 hover:underline"
        onClick={() => onEditStep(index)}
      >
        {t("editStep")}
      </button>
    );
  }

  return (
    <div className="space-y-8">
      <FormErrorSummary />
      <DescriptionList>
        <DescriptionTerm>
          {t("summaryRoute")}
          {editButton(0)}
        </DescriptionTerm>
        <DescriptionDetails>
          {departure?.supplierOffice.name ?? "…"} →{" "}
          {delivery?.supplierOffice.name ?? "…"}
          {additionalDestinations > 0 ? (
            <span className="ml-2 text-gray-500">
              {t("summaryAdditionalDestinations", {
                count: additionalDestinations,
              })}
            </span>
          ) : null}
        </DescriptionDetails>

        <DescriptionTerm>{t("summaryVehicle")}</DescriptionTerm>
        <DescriptionDetails>{vehicle?.vehicle.name ?? "…"}</DescriptionDetails>

        <DescriptionTerm>{t("summaryQuantity")}</DescriptionTerm>
        <DescriptionDetails>{values.quantity}</DescriptionDetails>

        <DescriptionTerm>
          {t("summaryAvailability")}
          {editButton(1)}
        </DescriptionTerm>
        <DescriptionDetails>
          {formatDate(values.available_from_date)} –{" "}
          {formatDate(values.available_to_date)}
        </DescriptionDetails>

        <DescriptionTerm>{t("summaryHire")}</DescriptionTerm>
        <DescriptionDetails>
          {t("summaryHireValue", {
            units: values.hire_units_allowed ?? 0,
            unit: unitLabel,
            extra: values.extra_hire_units_allowed ?? 0,
            minimum: values.minimum_hire_units ?? 0,
          })}
        </DescriptionDetails>

        <DescriptionTerm>{t("summaryDistance")}</DescriptionTerm>
        <DescriptionDetails>
          {values.distance_allowed
            ? `${values.distance_allowed} ${distanceUnit}`
            : t("summaryUnlimited")}
        </DescriptionDetails>

        <DescriptionTerm>
          {t("summaryInsurance")}
          {editButton(2)}
        </DescriptionTerm>
        <DescriptionDetails>
          {insurance?.supplierInsuranceOption.name ?? "…"}
        </DescriptionDetails>

        <DescriptionTerm>{t("summaryInclusions")}</DescriptionTerm>
        <DescriptionDetails>
          {inclusionLabels.length > 0
            ? inclusionLabels.join(", ")
            : t("summaryNone")}
        </DescriptionDetails>
      </DescriptionList>

      <div className="flex justify-center">
        <Button
          plain
          onClick={() => setShowAdvanced(!showAdvanced)}
          type="button"
        >
          <ChevronDownIcon
            data-slot="icon"
            className={clsx("transition-all duration-200", {
              "rotate-180": showAdvanced,
            })}
          />
          <span>{tForm("additionalSettings")}</span>
        </Button>
      </div>

      {showAdvanced ? (
        <div className="space-y-8">
          <AdditionalSettingsSections />
        </div>
      ) : null}
    </div>
  );
}
